import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, Tooltip, OverlayTrigger, Popover, Row, Badge, Table } from "react-bootstrap";
import { NavigationContext } from "../../Context/NavigationContext";
import { RightSideContext } from "../../Context/RightSideContext";
import { useAppSelector } from "../../redux/hooks";
import { useDispatch } from "react-redux";
import {
    setLastSaveTime,
    setInfileTasks,
    reloadUndertakings,
} from "../../redux/reducer";
import { CONST, fileTabVal } from "../../SideDrawer/sideTypes";
import { getDataReader } from "../../livedataFuncs";
import { getCurrentContentWindow, runInnerFunction } from "../../compatManager";
import { UndertakingXmlItem } from "./types";
import UndertakingEmailModal from "./UndertakingEmailModal";
import { DatePicker } from "../datePicker";
import type { UndertakingEmailDraft } from "./types";
import { // eeva: p- subset explosion; a- define schema + (node) parser  + field definition in types
    // undertakingTaskLinkKey,
    UNDERTAKING_LEGACY_DOC_TYPES,
    // UNDERTAKING_XML_ATTRS_AMOUNT,
    // UNDERTAKING_XML_ATTRS_COMPLETED_DATE,
    // UNDERTAKING_XML_ATTRS_COMPLETED_FLAG,
    // UNDERTAKING_XML_ATTRS_COMPLETED_NUMBER,
    // UNDERTAKING_XML_ATTRS_DEFECT_NAME,
    // UNDERTAKING_XML_ATTRS_EMAIL,
    // UNDERTAKING_XML_ATTRS_FOLLOW_UP_BY,
    // UNDERTAKING_XML_ATTRS_FOLLOW_UP_DATE,
    // UNDERTAKING_XML_ATTRS_INSTRUMENT,
    // UNDERTAKING_XML_ATTRS_LENDER,
    // UNDERTAKING_XML_ATTRS_SHORT_DESC,
    // UNDERTAKING_XML_ATTR_AUTO_SEND_EMAIL,
    type PSM,
    type UndertakingDocumentRow,
    type UndertakingIssueEditPayload,
    type UndertakingIssueReminderPayload,
    type UndertakingLegacyDocType,
    type XmlNodeLike,
} from "./types"; // / Eva: p- subset explosion; a- define schema + (node) parser  
import type { UndertakingIssue } from "./types";
import { mapUndertakingXmlItemToIssue } from "./types";
import {
    emailLegacyGeneratedDocByDoctypeId,
} from "./legacyDocumentAutomation";
import {
    dispatchActivityCentreAdd,
    dispatchActivityCentreDelete,
} from "./util/activityCentre";
import { lddGlobalSessionInfo, NavObjectType } from "../navigationTypes";
import { getBusinessDate } from "../../../../libs/taskmanagement/src/TaskModule";
import {
    buildDesc,
    finishFollowup,
    genDocument,
    getAllUndertaking,
    getMailList,
    getUndertakingDataNode,
    OffsetDays,
    saveUndertakings,
    UndertakingItemType,
} from "./UndertakingFuncs";
// import {
//     advanceUndertakingFollowUpTask,
//     mergeUndertakingTasks,
//     removeUndertakingLinkedTasks,
// } from "../taskSystem/undertakingTasks";
import { openToolbarSendEmailMain } from "./undertakingToolbarEmail";
import {
    dateToXMLDate,
    differenceInDays,
    FormatDateDataType,
    isXMLDate,
    xmlDateToDate,
} from "../../util/genericDateAndTimeFuncs";

/**
 * Undertaking dashboard: active/completed issues, defect popup integration, legacy doc Generate/Open/Email.
 * Sections: module constants & XML helpers → component state → derived (useMemo) → effects → defect/task handlers → doc actions → render.
 */

const isPSM = (value: string): value is PSM =>
    value === "P" || value === "S" || value === "M";

const DOCUMENTS: UndertakingDocumentRow[] = [
    { name: "Undertaking Discharge", id: "2230"},
    { name: "Undertaking Vendor Solicitor", id: "626"},
];


const LEGACY_DOCUMENT_GENERATE: Record<
    string,
    Partial<Pick<UndertakingLegacyDocType, "doctypeId" | "groupingId" | "label">> // Eeva partial before pick may skip if error; disaallow oartial, use :! in type def
> = {
    "Undertaking Discharge": {
        doctypeId: UNDERTAKING_LEGACY_DOC_TYPES.DischargeUndertaking.doctypeId,
        groupingId:
            UNDERTAKING_LEGACY_DOC_TYPES.DischargeUndertaking.groupingId,
        label: UNDERTAKING_LEGACY_DOC_TYPES.DischargeUndertaking.label,
    },
    "Undertaking Vendor Solicitor": {
        doctypeId: UNDERTAKING_LEGACY_DOC_TYPES.VendorSolicitor.doctypeId,
        groupingId: UNDERTAKING_LEGACY_DOC_TYPES.VendorSolicitor.groupingId,
        label: UNDERTAKING_LEGACY_DOC_TYPES.VendorSolicitor.label,
    },
};

type CompletePromptKind = "discharge-simple" | "discharge" | "related";

const COMPLETE_PROMPT_MESSAGE: Record<CompletePromptKind, string> = {
    "discharge-simple": "Are you sure you want to complete this Mortgage discharge and it's related security charge(s)?",
    "discharge": "Do you want to complete the Related Security Charge(s) for this undertaking?",
    "related": "Do you want to complete the Mortgage Discharge and other Related Security Discharges with this one?",
};

/** Auto undertaking email POST (`/undertakingEmail`) — console when flag is on. */
function isUndertakingAutoEmailDebugEnabled(): boolean {
    if (typeof window === "undefined") return false;
    const ls = window.localStorage;
    return (
        ls?.getItem("DEBUG_UNDERTAKING_AUTO_EMAIL") === "1" ||
        ls?.getItem("DEBUG_UNDERTAKING") === "1" ||
        ls?.getItem("DEBUG_UNDERTAKING_MERGE") === "1"
    );
}

// function undertakingDebugLog(...args: unknown[]): void {
//     if (!isUndertakingDebugEnabled()) return;
//     // eslint-disable-next-line no-console
//     console.log("[Undertaking]", ...args);
// }

// function undertakingAutoEmailDebugLog(...args: unknown[]): void {
//     if (!isUndertakingAutoEmailDebugEnabled()) return;
//     // eslint-disable-next-line no-console
//     console.log("[Undertaking auto-email]", ...args);
// }

// function postUndertakingEmailApi(
//     session: lddGlobalSessionInfo,
//     body: Record<string, unknown>,
// ): Promise<Response> {
//     const rw = process.env.REACT_APP_RWAPI_ENDPOINT;
//     const prov = String(session?.appProvinceShort ?? "");
//     const firmId = String(session?.firm?.id ?? "");
//     const sid = String(session?.sid ?? "");
//     const url = `/${rw}/api/${prov}/${firmId}/undertakingEmail?SessionID=${encodeURIComponent(sid)}`;
//     return fetch(url, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(body),
//     });
// }

// Supports both legacy MSXML (`selectNodes`/`selectSingleNode`) and standard DOM XPath (`evaluate`).

function selectSingleNodeCompat(
    contextNode: Document | XmlNodeLike | null,
    xpath: string,
): XmlNodeLike | null {
    if (!contextNode) return null;
    const node = contextNode as XmlNodeLike;
    if (typeof node.selectSingleNode === "function") {
        return node.selectSingleNode(xpath);
    }
    const doc = (contextNode as XmlNodeLike).ownerDocument || contextNode;
    if (doc && typeof (doc as Document).evaluate === "function") {
        const result = (doc as Document).evaluate(
            xpath,
            contextNode as unknown as Node,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        );
        return (result.singleNodeValue ?? null) as XmlNodeLike | null;
    }
    return null;
}

// function attr(node: XmlNodeLike | null, name: string): string | undefined {
//     return node?.getAttribute?.(name) ?? undefined;
// }

// function attrAny(
//     node: XmlNodeLike | null,
//     names: readonly string[],
// ): string | undefined {
//     for (const nm of names) {
//         const v = attr(node, nm);
//         if (v !== undefined && String(v).trim().length > 0) return String(v);
//     }
//     return undefined;
// }

// function parseUndertakingDate(raw?: string): Date | null {
// //     const s = (raw ?? "").trim();
// //     if (!s) return null;

// //     if (isXMLDate(s)) {
// //         const d = xmlDateToDate(s);
// //         return d && !Number.isNaN(d.getTime()) ? d : null;
// //     }

// //     const native = new Date(s);
// //     if (!Number.isNaN(native.getTime())) return native;

// //     const mdyOrDmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
// //     if (mdyOrDmy) {
// //         const a = Number(mdyOrDmy[1]);
// //         const b = Number(mdyOrDmy[2]);
// //         const y = Number(mdyOrDmy[3]);
// //         const day = a > 12 ? a : b;
// //         const month = a > 12 ? b : a;
// //         const d = new Date(y, month - 1, day);
// //         return Number.isNaN(d.getTime()) ? null : d;
// //     }

// //     const dMonY = /^(\d{1,2})[\s-]([A-Za-z]{3,})[\s-](\d{4})$/.exec(s);
// //     if (dMonY) {
// //         const day = Number(dMonY[1]);
// //         const mon = dMonY[2].slice(0, 3).toLowerCase();
// //         const year = Number(dMonY[3]);
// //         const monthIdx = MONTH_ABBREV_TO_INDEX[mon];
// //         if (monthIdx === undefined) return null;
// //         const d = new Date(year, monthIdx, day);
// //         return Number.isNaN(d.getTime()) ? null : d;
// //     }

// //     return null;
// }

// function readOneDefectToXmlItem(
//     n: XmlNodeLike,
//     data: XmlNodeLike | null,
//     i: number,
// ): UndertakingXmlItem {
//     const typeRaw = attr(n, "type");
//     const nameRaw = attrAny(n, UNDERTAKING_XML_ATTRS_DEFECT_NAME);
//     const shortDesc = attrAny(n, UNDERTAKING_XML_ATTRS_SHORT_DESC);
//     const instrumentNumber = attrAny(data, UNDERTAKING_XML_ATTRS_INSTRUMENT);
//     const lender = attrAny(data, UNDERTAKING_XML_ATTRS_LENDER);
//     const lenderNode = selectSingleNodeCompat(data, "Lender");
//     const lenderEmail = attrAny(lenderNode, UNDERTAKING_XML_ATTRS_EMAIL);
//     const amount = attrAny(data, UNDERTAKING_XML_ATTRS_AMOUNT);

//     const followUpDateRaw = attrAny(data, UNDERTAKING_XML_ATTRS_FOLLOW_UP_DATE);
//     const followUpBy = attrAny(data, UNDERTAKING_XML_ATTRS_FOLLOW_UP_BY);

//     const completedFlag =
//         attrAny(data, UNDERTAKING_XML_ATTRS_COMPLETED_FLAG) === "1" ||
//         attrAny(n, UNDERTAKING_XML_ATTRS_COMPLETED_FLAG) === "1";
//     const completedDateRaw =
//         attrAny(data, UNDERTAKING_XML_ATTRS_COMPLETED_DATE) ??
//         attrAny(n, UNDERTAKING_XML_ATTRS_COMPLETED_DATE);
//     const completedDate = parseUndertakingDate(completedDateRaw);
//     const completedByDate = Boolean(
//         completedDateRaw && completedDateRaw.trim().length > 0,
//     );

//     const completedNumber = attrAny(data, UNDERTAKING_XML_ATTRS_COMPLETED_NUMBER);
//     const autoSendRaw = attr(data, UNDERTAKING_XML_ATTR_AUTO_SEND_EMAIL);
//     const autoSendEmailEnabled =
//         autoSendRaw === undefined || String(autoSendRaw).trim() === ""
//             ? true
//             : String(autoSendRaw).trim() === "1";

//     // Build item in same order as UndertakingXmlItem (identity → content → flags → completion)
//     const item: UndertakingXmlItem = {
//         xmlIndex: i,
//         key: `${i}:${typeRaw || ""}:${attr(n, "name") || ""}`,
//         defectId: attr(n, "DefectId"),
//         followUpAnchorDate: attr(n, "UndertakingFollowUpAnchorDate"),
//         typeRaw: typeRaw ?? undefined,
//         title: nameRaw ?? undefined,
//         summary: shortDesc ?? undefined,
//         instrumentNumber,
//         lender,
//         lenderEmail,
//         amount,
//         isActiveTab: n?.getAttribute?.("activeTaskName") === "Undertaking",
//         vendor: data?.getAttribute?.("VendorUndertaking") === "1",
//         vendorLawyer: data?.getAttribute?.("VendorLawyerUndertaking") === "1",
//         completed: completedFlag || completedByDate,
//         completedDate,
//         completedNumber,
//         lastFollowUpDate: parseUndertakingDate(followUpDateRaw),
//         lastFollowUpBy: followUpBy ?? undefined,
//         autoSendEmailEnabled,
//     };
//     return item;
// }

// export function readUndertakingsFromXml(xmlDoc: Document | XmlNodeLike): UndertakingXmlItem[] {
//     const nodes = selectNodesCompat(xmlDoc, "//Defect[@Undertaking='1']");
//     const out: UndertakingXmlItem[] = [];
//     for (let i = 0; i < nodes.length; i++) {
//         const n = nodes[i];
//         const data = selectSingleNodeCompat(n, "Data");
//         out.push(readOneDefectToXmlItem(n, data, i));
//     }
//     return out;
// }

// function setUndertakingDefectDataAttributeByXmlIndex(
//     dataroot: XmlNodeLike | null | undefined,
//     xmlIndex: number,
//     attrName: string,
//     value: string,
// ): boolean {
//     if (!dataroot) return false;
//     const xmlDoc = (dataroot as XmlNodeLike).ownerDocument ?? dataroot;
//     const nodes = selectNodesCompat(xmlDoc, "//Defect[@Undertaking='1']");
//     const defectNode = nodes[xmlIndex];
//     if (!defectNode) return false;
//     const data = selectSingleNodeCompat(defectNode, "Data");
//     if (!data || typeof data.setAttribute !== "function") return false;
//     data.setAttribute(attrName, value);
//     return true;
// }

// function fillTemplate(
//     template: string,
//     ctx: { undertakingTitle: string; propertyAddress: string },
// ): string {
//     let out = template ?? "";

//     out = out.replace(
//         /\[undertaking_name\]/gi,
//         ctx.undertakingTitle || "[undertaking_name]",
//     );

//     if (ctx.propertyAddress) {
//         out = out.replace(/\[address\]/gi, ctx.propertyAddress);
//     } else {
//         out = out
//             .replace(/\s*-\s*\[address\]\s*/gi, " ")
//             .replace(/\[address\]/gi, "");
//     }

//     return out.replace(/\s{2,}/g, " ").trim();
// }

// function formatUiDate(date: Date | null | undefined): string { // Eva: check to reuse
//     if (!date || Number.isNaN(date.getTime())) return "—";
//     const day = String(date.getDate()).padStart(2, "0");
//     const month = date
//         .toLocaleString(undefined, { month: "short" })
//         .toUpperCase();
//     const year = date.getFullYear();
//     return `${day}/${month}/${year}`;
// }

function addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

// function isTemplateLikeSummary(s: string): boolean {
//     return /<\w+|\bxpath=|\bxcondition=|xmlbase=|<\/span>/i.test(s);
// }

// function buildUndertakingSummary(u: UndertakingXmlItem): string {
//     const raw = (u.summary ?? "").trim();
//     if (raw.length > 0 && !isTemplateLikeSummary(raw)) return raw;

//     const lender = (u.lender ?? "").trim();
//     const instr = (u.instrumentNumber ?? "").trim();
//     const amount = (u.amount ?? "").trim();

//     if (lender.length > 0 || instr.length > 0) {
//         const instrPart =
//             instr.length > 0 ? `Instrument No. ${instr} ` : "This ";
//         const lenderPart =
//             lender.length > 0
//                 ? `in favour of ${lender}`
//                 : "in favour of the lender";
//         const amountPart =
//             amount.length > 0
//                 ? ` securing the principal amount of ${amount}`
//                 : "";
//         return `${instrPart}is a mortgage ${lenderPart}${amountPart}.`;
//     }

//     const flags = [
//         u.vendor ? "Vendor" : null,
//         u.vendorLawyer ? "Vendor Lawyer" : null,
//     ]
//         .filter((x): x is string => Boolean(x))
//         .join(" • ");
//     return flags.length > 0 ? flags : "Undertaking";
// }

function xpathLiteral(value: string): string {
    // XPath string escaping: use quotes when possible; fall back to concat() only when needed.
    if (!value.includes("'")) return `'${value}'`;
    if (!value.includes('"')) return `"${value}"`;
    return `concat('${value.replace(/'/g, "',\"'\",'")}')`;
}

const IssueDesc = ({text, maxLength = 180}) => {
  const [open, setOpen] = useState(false);

  const isLong = text.length > maxLength;
  const displayText = open ? text : text.slice(0, maxLength) + (isLong ? "..." : "");

  return (
    <>
      <div>{displayText}</div>

      {isLong && (
        <Button
          variant="link"
          className="p-0"
          onClick={() => setOpen(!open)}
          aria-controls="expand-text"
          aria-expanded={open}
        >
          {open ? "show less" : "show more"}
        </Button>
      )}
    </>
  );
}


const Undertaking: React.FC = () => { 
    // ——— Context & state ———
    const nav = useContext(NavigationContext);
    const rightSide = useContext(RightSideContext);
    const page = useAppSelector((state) => state.currentNavigation.page);
    const dataroot = useAppSelector((state) => state.currentNavigation.livedataRootContainer.dataRoot,);
    const SESSION_INFO = useAppSelector((g) => g.SessionInfo as lddGlobalSessionInfo);
    const lastSaveTime = useAppSelector((s) => s.LastSaveTime);
    const dispatch = useDispatch();

    const provinceCode = SESSION_INFO.appProvinceShort as string
    const dataReaderObj = useMemo(() => getDataReader(dataroot), [dataroot]);
    const appId = useMemo(() => dataReaderObj.getAppId(), [dataReaderObj]);
    const [emailDraft, setEmailDraft] = useState<UndertakingEmailDraft | null>(
        null,
    );
    const [generatingDocName, setGeneratingDocName] = useState<string | null>(
        null,
    );
    /** Known "no generated doc" per row after a failed Open/Email; cleared on generate / file change. */
    const [openableDocs, setOpenableDocs] = useState<Record<string, boolean>>({});
    const redUpdate = useAppSelector((state) => state.UndertakingReload);
    const undertakingPrefs = useAppSelector((state) => state.UndertakingPreferences) as Record<string, any> | undefined;
    const PSM = dataroot?.getAttribute("PSM");
    const closing = dataroot?.getAttribute("ClosingDate")
    const [undertakings, SetUndertakings] = useState<UndertakingItemType[]>([])

    const [reget, setReget] = useState(1);
    type CompletePrompt = {
        kind: CompletePromptKind;
        item: UndertakingItemType;
        primaryId: string;
        extraIds: string[];
    };
    type RegPrompt = { item: UndertakingItemType; ids: string[] };
    const [completePrompt, setCompletePrompt] = useState<CompletePrompt | null>(null);
    const [regPrompt, setRegPrompt] = useState<RegPrompt | null>(null);
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [activeItem, setActiveItem] = useState<UndertakingItemType>(null);
    const [regNumInput, setRegNumInput] = useState("");
    const [regDateInput, setRegDateInput] = useState("");
    const activeUndertakings = undertakings.filter(i => !(i.CompletedDate > ""));
    const completedUndertakings = undertakings.filter(i => i.CompletedDate > "");
    const user = SESSION_INFO.user.fullname;
    const [sendCheck, setSendCheck] = useState(false);
    const currDate = (new Date()).toISOString().substring(0, 10);
    const dueDate = OffsetDays(dataReaderObj.getClosingDate(), 60, provinceCode) || "No Closing Date";
    const docs = {"Undertaking Discharge": "2230", "Undertaking Vendor Solicitor": "626"}
    const autoEnabled = sendCheck

    const propertyAddress = dataroot.getAttribute("PropertyAddress") || "";

    useEffect(() => {
        let override = dataroot.getAttribute("DisableUndertakingEmail");
        if(override > "") setSendCheck(override !== "1");
        else setSendCheck(undertakingPrefs.enableFu && undertakingPrefs.emailFu || false);
    }, []);

    useEffect(() => {
        // console.log("re get undertakings")
        SetUndertakings(getAllUndertaking(dataroot));

    }, [reget, redUpdate])

    const update = () => {
        setReget(r => r + 1);
        runInnerFunction("SetPageDirty", null);
    }

    // Write attribute(s) on a Defect node in both the Redux dataroot and the legacy iframe's
    // livedataRoot when they are not the same reference. Without this, legacy popups like
    // Registration Details read from a stale tree until the user touches them.
    const setDefectAttrs = (defectId: string, attrs: Record<string, string>) => {
        const xpath = `Defect[@DefectId='${defectId}']`;
        const apply = (root: any) => {
            const node = root?.selectSingleNode?.(xpath);
            if(!node) return;
            for(const [name, value] of Object.entries(attrs)){
                node.setAttribute(name, value);
            }
        };
        apply(dataroot);
        const iframeRoot = (getCurrentContentWindow() as any)?.livedataRoot;
        if(iframeRoot && iframeRoot !== dataroot) apply(iframeRoot);
    };

    useEffect(() => {
        const refresh = () => setReget(r => r + 1);
        window.addEventListener("LDDDialogClosed", refresh);
        return () => window.removeEventListener("LDDDialogClosed", refresh);
    }, []);

    const completeDefect = (defectId: string) => {
        const data = dataroot.selectSingleNode(`Defect[@DefectId='${defectId}']`)?.selectSingleNode('Data');
        if(!data) return;
        data.setAttribute('DateCompliedWith', currDate);
        const follow = getUndertakingDataNode(defectId, dataroot);
        follow.setAttribute("CompletedBy", user);
        for(let i of [1, 2]){
            if(!(follow.getAttribute(`SentDate${i}`) > "")){
                // manually completed without followup sent- delete the task
                follow.setAttribute(`SentDate${i}`, currDate);
                follow.setAttribute(`TaskActive${i}`, "0");
            }
        }
    };

    const collectLinkedDischargeIds = (refNum: string, excludeId: string): string[] => {
        const xpath = `Defect[Data/@InstrumentNumber='${refNum}'] | Defect[Data/@AssociatedRefNum='${refNum}']`;
        return (dataroot.selectNodes(xpath) || [])
            .filter((n: any) => {
                const id = n.getAttribute("DefectId");
                if(!id || id === excludeId) return false;
                const data = n.selectSingleNode("Data");
                const hasInstrument = !!data?.getAttribute("InstrumentNumber");
                const isOwnUndertaking = n.getAttribute("Undertaking") === "1"
                    && (PSM === "M" || data?.getAttribute("VendorLawyerUndertaking") === "1");
                return hasInstrument || isOwnUndertaking;
            })
            .map((n: any) => n.getAttribute("DefectId") as string);
    };

    const buildCompletePrompt = (item: UndertakingItemType): CompletePrompt | null => {
        const related = item.relatedSecurityCharges ?? [];
        const ownRelatedIds = related.filter(r => r.isOwnUndertaking).map(r => r.id);
        const base = { item, primaryId: item.id };
        if(item.isMortgageDischarge && ownRelatedIds.length > 0){
            return { ...base, kind: "discharge", extraIds: ownRelatedIds };
        }
        if(item.isMortgageDischarge && related.length > 0){
            return { ...base, kind: "discharge-simple", extraIds: related.map(r => r.id) };
        }
        if(item.associatedRefNum){
            return { ...base, kind: "related", extraIds: collectLinkedDischargeIds(item.associatedRefNum, item.id) };
        }
        return null;
    };

    const runCompletion = (item: UndertakingItemType, ids: string[]) => {
        if(item.isMortgageDischarge && (!item.registeredSecurityNum || !item.registeredSecurityDate)){
            setRegNumInput(item.registeredSecurityNum || "");
            setRegDateInput(item.registeredSecurityDate || "");
            setRegPrompt({ item, ids });
            return;
        }
        ids.forEach(completeDefect);
        update();
    };

    const handleCompleteClick = (item: UndertakingItemType) => {
        setActiveItem(item);
        const prompt = buildCompletePrompt(item);
        if(prompt) setCompletePrompt(prompt);
        else runCompletion(item, [item.id]);
    };

    const resolveCompletePrompt = (includeExtras: boolean) => {
        if(!completePrompt) return;
        const { item, primaryId, extraIds } = completePrompt;
        setCompletePrompt(null);
        runCompletion(item, includeExtras ? [primaryId, ...extraIds] : [primaryId]);
    };

    const resolveRegPrompt = (save: boolean) => {
        if(!regPrompt) return;
        const { item, ids } = regPrompt;
        if(save){
            setDefectAttrs(item.id, {
                DischargeInstrumentNumber: regNumInput,
                DischargeRegistrationDate: regDateInput,
                // Legacy date Field has xpath="../@ClosingDate" fallback; without this
                // flag the form ignores our attribute and shows ClosingDate instead.
                isDischargeRegistrationDateOverWrite: "1",
            });
            ids.forEach(completeDefect);
            setShowNotifyModal(true);
        }
        setRegPrompt(null);
        update();
    };

    const editDefect = (defectId: string) => {
        const defectNode = dataroot.selectSingleNode(`Defect[@DefectId='${defectId}']`);
        if(!defectNode) return;
        const popup: NavObjectType = {
            component: "main",
            url: "lfpopup.asp",
            popup: true,
            popupStyle: "xlarge",
            isModal: true,
            params: {
                compat: "1",
                NoHeader: "1",
                XSLTParamValue: defectNode.getAttribute("type"),
                XSLTParamName: "paramType",
                xslt: "private/defectasks",
                xml: "private/DefectList",
            },
            windowState: {
                dialogArguments: {
                    datalockobj: {},
                    liveformPopupDataNode: defectNode,
                    location: {},
                },
            },
            callback: (val: any) => {
                if(val === false){
                    defectNode.parentNode?.removeChild(defectNode);
                }
                update();
            },
        };
        nav(popup);
    }

    // this cannot detect changes
    // useEffect(() => {
    //     if (!dataroot) return;
    //     if (ensureUndertakingDefectIds(dataroot)) {
    //         setUndertakingIssuesRevision((n) => n + 1);
    //     }
    // }, [dataroot]);
    const activityTrackerRef = useRef<number | null>(null);

    // ——— Activity Centre (load/success/error toasts) ———
    const activityCenterAdd = (
        msg: string,
        type: "load" | "loading" | "success" | "error",
        tracker: number,
    ) =>
        dispatchActivityCentreAdd({
            msg,
            type,
            lddActivityCenterTracker: tracker,
            clearWhenOut: true,
        });

    const activityCenterDelete = (tracker: number) =>
        dispatchActivityCentreDelete(tracker);

    // const undertakingEnableAutoFromStore = useAppSelector(
    //     (s) => s.UndertakingEnableAuto,
    // );
    // const undertakingTaskReminderPrefsFromStore = useAppSelector(
    //     (s) => s.UndertakingTaskReminderPrefs,
    // );
    // const {
    //     firstDays: undertakingFirstDays,
    //     secDays: undertakingSecDays,
    //     complyDays: undertakingComplyDays,
    //     firstFollowEnable: undertakingFirstFollowEnable,
    //     complyEnable: undertakingComplyEnable,
    //     followUpTaskRemind: undertakingFollowUpTaskRemind,
    //     complyTaskRemind: undertakingComplyTaskRemind,
    //     enableAuto: undertakingEnableAutoFromPrep,
    // } = useUndertakingPrepPrefs();
    // const mergeUndertakingEnableAuto =
    //     undertakingEnableAutoFromStore !== undefined
    //         ? undertakingEnableAutoFromStore
    //         : undertakingEnableAutoFromPrep ?? 1;
    // const mergeFollowUpTaskRemind =
    //     undertakingTaskReminderPrefsFromStore?.followUpTaskRemind !== undefined
    //         ? undertakingTaskReminderPrefsFromStore.followUpTaskRemind
    //         : undertakingFollowUpTaskRemind ?? 1;
    // const mergeComplyTaskRemind =
    //     undertakingTaskReminderPrefsFromStore?.complyTaskRemind !== undefined
    //         ? undertakingTaskReminderPrefsFromStore.complyTaskRemind
    //         : undertakingComplyTaskRemind ?? 1;
    // const mergeFirstFollowEnable =
    //     undertakingTaskReminderPrefsFromStore?.firstFollowEnable !== undefined
    //         ? undertakingTaskReminderPrefsFromStore.firstFollowEnable
    //         : undertakingFirstFollowEnable ?? 1;
    // const mergeComplyEnable =
    //     undertakingTaskReminderPrefsFromStore?.complyEnable !== undefined
    //         ? undertakingTaskReminderPrefsFromStore.complyEnable
    //         : undertakingComplyEnable ?? 1;

    // ——— Derived: file type, app id, issues, visible docs ———
    /** App file type (P/S/M/O). From util/rpwTypes (PSM). Used by Follow up (email pop-out) and file generation. */
    // const fileType: PSM | "" = useMemo(() => {
    //     const psm = String(page?.params?.PSM ?? "").trim().toUpperCase();
    //     return isPSM(psm) ? psm : "";
    // }, [page?.params?.PSM]);

    const currentApp = useMemo(() => {
        const idFromParams = String(page?.params?.id ?? "").trim();
        const psmFromParams = String(page?.params?.PSM ?? "").trim();
        let id = idFromParams;
        let PSM = psmFromParams;
        if (dataroot && (!id || !PSM)) {
            const reader = getDataReader(dataroot);
            if (!id) id = String(reader.getAppId?.() ?? "").trim();
            if (!PSM) PSM = String(reader.getPSM?.() ?? "").trim();
        }
        return {
            id: id || CONST.uninitialized,
            PSM: PSM || CONST.uninitialized,
        };
    }, [page?.params?.id, page?.params?.PSM, dataroot]);

    // const followUpActorName = useMemo(() => {
    //     const u = SESSION_INFO?.user;

    //     const full = u.fullname?.trim();
    //     if (full) return full;
    //     return "" // fallback with empty string 
    
    // }, [SESSION_INFO]);

    // const baseIssues = useMemo(() => {
    //     if (!dataroot)
    //         return [] as UndertakingIssue[];
    //     // (Pre-requisite) Active vs Completed undertakings:
    //     // Both are marked as active, but completed undertakings have a sepcified "Date Undertaking Complied With".

    //     // Data flow: On Active-Completed dashboard, use in-memory data (live XML in `dataroot`/`ownerDocument`):
    //     // The node `Data/@DateCompliedWith` reads "Date Undertaking Complied With" and marks an item as completed.
    //     const reader = getDataReader(dataroot);
    //     const closingDateRaw = reader.getClosingDate?.();
    //     const closingDate = closingDateRaw ? new Date(closingDateRaw) : null;
    //     const dueDate = closingDate
    //         ? addDays(closingDate, undertakingFirstDays)
    //         : null; // closing + firstDays (prefs or DEFAULT_FIRST_DAYS)

    //     const xmlDoc: Document | XmlNodeLike =
    //         (dataroot as XmlNodeLike)?.ownerDocument ?? (dataroot as XmlNodeLike);
    //     // Dashboard cards: only defects with Vendor's Solicitor's Undertaking (Data/@VendorLawyerUndertaking='1') (see readOneDefectToXmlItem.vendorLawyer).
    //     const undertakings = readUndertakingsFromXml(xmlDoc).filter(
    //         (u) => u.vendorLawyer,
    //     );

    //     return undertakings.map((u) => {
    //         const title = (u.title ?? "").trim() || "Undertaking";
    //         // const summary = buildUndertakingSummary(u);
    //         let dataNode = dataroot?.selectSingleNode(`Defect[@DefectId='${u.defectId}']`)?.selectSingleNode('Data')
    //         const summary = buildDesc(dataNode);
    //         return mapUndertakingXmlItemToIssue({
    //             item: u,
    //             title,
    //             summary,
    //             dueDate,
    //         });
    //     });
    // }, [
    //     dataroot,
    //     undertakingFirstDays,
    //     undertakingIssuesRevision,
    //     lastSaveTime,
    // ]);

    // const { activeIssues, completedIssues } = useMemo(() => {
    //     const issues: UndertakingIssue[] = baseIssues.map((issue) => {
    //         const pk = undertakingTaskLinkKey(issue);
    //         const pend =
    //             undertakingPendingFollowUpXml?.[pk] ??
    //             undertakingPendingFollowUpXml?.[issue.taskNotesKey] ??
    //             undertakingPendingFollowUpXml?.[issue.key];
    //         if (!pend) return issue;
    //         const raw = pend.lastFollowUpDate?.trim() ?? "";
    //         let overlayDate: Date | null = null;
    //         if (raw) {
    //             if (isXMLDate(raw)) {
    //                 const d = xmlDateToDate(raw);
    //                 overlayDate =
    //                     d && !Number.isNaN(d.getTime()) ? d : null;
    //             } else {
    //                 const d = new Date(raw);
    //                 overlayDate = Number.isNaN(d.getTime()) ? null : d;
    //             }
    //         }
    //         return {
    //             ...issue,
    //             lastFollowUpDate: overlayDate ?? issue.lastFollowUpDate,
    //             lastFollowUpBy: pend.lastFollowUpBy || issue.lastFollowUpBy,
    //         };
    //     });
    //     return {
    //         activeIssues: issues.filter((x) => !x.completed),
    //         completedIssues: issues.filter((x) => x.completed),
    //     };
    // }, [
    //     baseIssues,
    //     undertakingPendingFollowUpXml,
    // ]);

    // const mergedFileTasksForUndertakingDashboard = useMemo(() => { //eeva auto email count down 
    //     const baseTasks = inFileTasks ?? [];
    //     if (!dataroot) return baseTasks;
    //     const rootLike = dataroot as XmlNodeLike;
    //     const xmlDoc = (rootLike.ownerDocument ?? dataroot) as
    //         | Document
    //         | XmlNodeLike;
    //     const undertakings = enrichUndertakingItemsWithPersistedTaskDues(
    //         readUndertakingsFromXml(xmlDoc),
    //         xmlDoc,
    //     );
    //     return mergeUndertakingTasks( // eeva: clean up; adding for mortage discharge auto email count down 
    //         baseTasks,
    //         undertakings,
    //         {
    //             firstDays: undertakingFirstDays ?? 60,
    //             secDays: undertakingSecDays ?? 30,
    //             complyDays: undertakingComplyDays ?? 60,
    //             firstFollowEnable: mergeFirstFollowEnable as 0 | 1,
    //             complyEnable: mergeComplyEnable as 0 | 1,
    //             followUpTaskRemind: mergeFollowUpTaskRemind as 0 | 1,
    //             complyTaskRemind: mergeComplyTaskRemind as 0 | 1,
    //             enableAuto: mergeUndertakingEnableAuto as 0 | 1,
    //         },
    //         dataReaderObj,
    //         dataReaderObj.getFirmContactID?.()?.[0],
    //     );
    // }, [
    //     inFileTasks,
    //     dataroot,
    //     undertakingFirstDays,
    //     undertakingSecDays,
    //     undertakingComplyDays,
    //     mergeFirstFollowEnable,
    //     mergeComplyEnable,
    //     mergeFollowUpTaskRemind,
    //     mergeComplyTaskRemind,
    //     mergeUndertakingEnableAuto,
    //     dataReaderObj,
    //     undertakingIssuesRevision,
    //     lastSaveTime,
    // ]);

    // const hasDischargeIssue = useMemo(() => {
    //     const all = [...activeIssues, ...completedIssues];
    //     return all.some(
    //         (i) => String(i.title ?? "").trim().toLowerCase() === "discharge",
    //     );
    // }, [activeIssues, completedIssues]);

    // const visibleDocuments = useMemo(() => {
    //     return DOCUMENTS.filter((d) => {
    //         // if (d.name === "Undertaking Discharge") return hasDischargeIssue;
    //         return true;
    //     });
    // }, []);

    // useEffect(() => {
    //     setOpenableDocs({});
    // }, [currentApp.id]);
    // ——— Handlers: defect popup & task switching (All Issues, Edit, Reminder, Follow-up) ———
    // const goToAllIssues = useCallback(() => {

    //     // Eva debug
    //     undertakingDebugLog("goToAllIssues");

    //     let navObj: NavObjectType = {
    //         component: "main",
    //         url: "lfmaster.asp",
    //         params: {
    //             "tb": "TPAppXML",
    //             "formasp": "purfileedit",
    //             "id": dataReaderObj.getAppId(),
    //             "PSM": dataReaderObj.getPSM(),
    //         },
    //         controller: {tabName: "Issues", tabNumber: 0},
    //         label: "Issues",
    //     }
    //     nav(navObj);
    // }, [nav, dataReaderObj]);

    // const openIssueTaskInIFrame = (taskName: string, targetWindow?: any) => {
    //     // Assumption/dependency: the current compat frame (or popup) exposes legacy liveform APIs:
    //     // - `setXMLDataToForm(xmlNode, holder, ...)`
    //     // - `livedataRoot` / `xmlbaseNode`
    //     // - a `contentHolder` element (or `#contentHolder`)
    //     const w: any = targetWindow ?? getCurrentContentWindow();
    //     const livedataRoot: any = w?.livedataRoot ?? null;
    //     const root: any =
    //         livedataRoot?.nodeName === "Defect"
    //             ? livedataRoot
    //             : livedataRoot ?? w?.xmlbaseNode ?? null;
    //     const holder: any =
    //         w?.contentHolder ??
    //         w?.document?.getElementById?.("contentHolder") ??
    //         null;

    //     if (!w || !root || !holder || typeof w?.setXMLDataToForm !== "function")
    //         return false;

    //     const safeSetAttribute = (node: XmlNodeLike | null, name: string, value: string): boolean => {
    //         if (node == null || typeof node.setAttribute !== "function") return false;
    //         try {
    //             node.setAttribute(name, value);
    //             return true;
    //         } catch {
    //             return false;
    //         }
    //     };
    //     /** Set task as enabled (taskName=1) and active (activeTaskName=taskName) on node. Returns false if any set failed. */
    //     const setActiveTaskOnNode = (node: XmlNodeLike | null, task: string): boolean =>
    //         safeSetAttribute(node, task, "1") && safeSetAttribute(node, "activeTaskName", task);

    //     try {
    //         // For the defect popup, `activeTaskName` must be set on the Defect node (`livedataRoot`).
    //         const rootOk = setActiveTaskOnNode(root, taskName);
    //         const livedataOk =
    //             !(livedataRoot && livedataRoot !== root) || setActiveTaskOnNode(livedataRoot, taskName);
    //         if (!rootOk || !livedataOk) {
    //             goToAllIssues();
    //             return false;
    //         }

    //         w.setXMLDataToForm(root, holder, false, true);
    //         return true;
    //     } catch {
    //         return false;
    //     }
    // };
    
    // Legacy integration: wrap `w.onDataLoaded` to run `afterLoad()` after liveform data is loaded.
    // Warning: global monkey‑patch; can conflict with other code in the same window.
    // const hookAfterDataLoaded = (w: any, afterLoad: () => void) => {
    //     if (!w) return;
    //     const prev = w.onDataLoaded;
    //     w.onDataLoaded = () => {
    //         try {
    //             if (typeof prev === "function") prev();
    //         } catch {
    //             // Fall back to "All Issues" tab if popup/task switching fails.
    //             goToAllIssues();
    //             return;
    //         }
    //         try {
    //             afterLoad();
    //         } catch {
    //             // Fall back to "All Issues" tab if popup/task switching fails.
    //             goToAllIssues();
    //             return;
    //         }
    //     };
    // };

    // const trySwitchToUndertakingTask = (w: any) => {
    //     // Assumption/dependency: in the defect popup context, `w.livedataRoot` is the actual `Defect`
    //     // node rendered by the defect XSLT, and it honors `@activeTaskName` for the active tab.
    //     const nodeName = String(w?.livedataRoot?.nodeName ?? "");
    //     if (nodeName !== "Defect") return false;
    //     return openIssueTaskInIFrame("Undertaking", w);
    // };

    // const openSpecificUndertakingIssueFromIssues = useCallback((
    //     issue: UndertakingIssueEditPayload,
    // ) => {
    //     // Resolve the Defect from in-memory Redux `dataroot` so we do not depend on the legacy Issues
    //     // iframe having populated `livedataRoot` (avoids the full “all issues” list render path).
    //     if (!dataroot) return;

    //     const xmlDoc: Document | XmlNodeLike =
    //         (dataroot as XmlNodeLike)?.ownerDocument ?? (dataroot as XmlNodeLike);
    //     const root: XmlNodeLike = xmlDoc as XmlNodeLike;

    //     let defectNode: XmlNodeLike | null = null;
    //     const defectId = String((issue as any).defectId ?? "").trim();

    //     if (defectId.length > 0) {
    //         const followup = getUndertakingDataNode(defectId, dataroot);
    //         const dataEl = followup?.parentNode ?? null;
    //         defectNode =
    //             dataEl &&
    //             String(dataEl.nodeName) === "Data" &&
    //             dataEl.parentNode &&
    //             String(dataEl.parentNode.nodeName) === "Defect"
    //                 ? (dataEl.parentNode as XmlNodeLike)
    //                 : null;
    //     }

    //     const instr = String(issue.instrumentNumber ?? "").trim();

    //     if (!defectNode && instr.length > 0) {
    //         const instrLit = xpathLiteral(instr);
    //         defectNode = selectSingleNodeCompat(
    //             root,
    //             `//Defect[@Undertaking='1' and (Data/@InstrumentNumber=${instrLit} or @InstrumentNumber=${instrLit})]`,
    //         );
    //     }
    //     if (!defectNode) {
    //         defectNode = selectSingleNodeCompat(
    //             root,
    //             `(//Defect[@Undertaking='1'])[${Number(issue.xmlIndex) + 1}]`,
    //         );
    //     }

    //     if (!defectNode) {
    //         goToAllIssues();
    //         return;
    //     }

    //     try {
    //         defectNode.setAttribute("Undertaking", "1");
    //         defectNode.setAttribute("activeTaskName", "Undertaking");
    //         const anchorExisting = defectNode.getAttribute?.(
    //             "UndertakingFollowUpAnchorDate",
    //         );
    //         if (!anchorExisting || !String(anchorExisting).trim()) {
    //             defectNode.setAttribute(
    //                 "UndertakingFollowUpAnchorDate",
    //                 dateToXMLDate(new Date()),
    //             );
    //         }
    //     } catch {
    //         goToAllIssues();
    //         return;
    //     }

        // const navIssuesFrameIfNeeded = (): void => {
        //     const navObj: NavObjectType = {
        //         component: "main",
        //         url: "lfmaster.asp",
        //         params: {
        //             tb: "TPAppXML",
        //             formasp: "purfileedit",
        //             id: dataReaderObj.getAppId(),
        //             PSM: dataReaderObj.getPSM(),
        //         },
        //         controller: { tabName: "Issues", tabNumber: 0 },
        //         label: "Issues",
        //     };
        //     nav(navObj);
        // };

    //     const runOpen = (w: LegacyDefectEditorWindow) => {
    //         hookAfterDataLoaded(w, () => {
    //             trySwitchToUndertakingTask(w);
    //         });
    //         const maybePopup = setLiveformPopupDataNodeAndEditDefect(w, defectNode);
    //         const popupWindow: any =
    //             (maybePopup as { contentWindow?: Window })?.contentWindow ??
    //             maybePopup ??
    //             null;
    //         if (popupWindow && popupWindow !== w) {
    //             hookAfterDataLoaded(popupWindow, () => {
    //                 trySwitchToUndertakingTask(popupWindow);
    //             });
    //             window.setTimeout(
    //                 () => trySwitchToUndertakingTask(popupWindow),
    //                 0,
    //             );
    //         }
    //         window.setTimeout(() => trySwitchToUndertakingTask(w), 0);
    //     };

    //     /** Tight polling first (frame often ready in under ~1s), then back off — cap ~8s total. */
    //     const maxAttempts = 40;

    //     // Always navigate to the Issues / purfileedit frame first. `EditExistingDefect` can exist on the
    //     // current content window while the visible tab is not Issues (e.g. Undertaking React over another
    //     // workspace); skipping `nav()` then produced no main-frame navigation and a no-op or wrong popup.
    //     navIssuesFrameIfNeeded();

    //     const tryRunWhenReady = (attempt: number) => {
    //         const w = getCurrentContentWindow() as LegacyDefectEditorWindow | null;
    //         if (w && typeof w.EditExistingDefect === "function") {
    //             runOpen(w);
    //             return;
    //         }
    //         if (attempt >= maxAttempts) return;
    //         const delayMs = attempt < 20 ? 50 : 200;
    //         window.setTimeout(() => tryRunWhenReady(attempt + 1), delayMs);
    //     };

    //     window.setTimeout(() => tryRunWhenReady(0), 0);
    // }, [nav, dataReaderObj, goToAllIssues, dataroot]);

    // const getOtherSideLawyerEmail = useCallback((): string => {
    //     if (!dataroot) return "";
    //     const reader = getDataReader(dataroot);
    //     return (
    //         String(reader.getOtherSideLawyerField?.("Email") ?? "").trim() ||
    //         String(reader.getOtherSideLawyerField?.("FirmEmail") ?? "").trim()
    //     );
    // }, [dataroot]);

    // const getPropertyAddress = useCallback((): string => {
    //     if (!dataroot) return "";
    //     const reader = getDataReader(dataroot);
    //     return String(reader.getPropertyAddress?.() ?? "").trim();
    // }, [dataroot]);

    // const getLawyerEmail = useCallback((): string => {
    //     if (!dataroot) return "";
    //     const lawyerNode =
    //         (dataroot as XmlNodeLike).selectSingleNode?.("lawyer") ??
    //         selectSingleNodeCompat(dataroot as XmlNodeLike, "lawyer");
    //     return String(
    //         lawyerNode?.getAttribute?.("Email") ??
    //             lawyerNode?.getAttribute?.("FirmEmail") ??
    //             "",
    //     ).trim();
    // }, [dataroot]);

    // const getOtherSideFirmContactEmail = useCallback((): string => {
    //     if (!dataroot) return "";
    //     const solicitor =
    //         (dataroot as XmlNodeLike).selectSingleNode?.("Solicitor") ??
    //         selectSingleNodeCompat(dataroot as XmlNodeLike, "Solicitor");
    //     const mainContact =
    //         solicitor?.selectSingleNode?.("ContactPeople[@MainContact='1']") ??
    //         (solicitor ? selectSingleNodeCompat(solicitor, "ContactPeople[@MainContact='1']") : null);

    //     const email = String(
    //         mainContact?.getAttribute?.("Email") ??
    //             mainContact?.getAttribute?.("FirmEmail") ??
    //             solicitor?.getAttribute?.("Email") ??
    //             solicitor?.getAttribute?.("FirmEmail") ??
    //             "",
    //     ).trim();

    //     return email || getOtherSideLawyerEmail();
    // }, [dataroot, getOtherSideLawyerEmail]);

    const openEmailDraft = useCallback((
        kind: "seller" | "lender" | "buyer",
        issue: any,
    ) => {
        const ctx = {
            undertakingTitle: String(issue.name ?? "").trim(),
            propertyAddress: propertyAddress,
        };

        let to =
            kind === "lender"
                ? String(issue.lenderEmail ?? "").trim()
                : dataReaderObj.getOtherSideLawyerField("Email");
        if (kind === "lender" && !to && dataroot) {
            const instr = String(issue.instrumentNumber ?? "").trim();
            if (instr) {
                const xmlDoc: Document | XmlNodeLike =
                    (dataroot as XmlNodeLike)?.ownerDocument ?? (dataroot as XmlNodeLike);
                const instrLit = xpathLiteral(instr);
                const mtg =
                    selectSingleNodeCompat(
                        xmlDoc,
                        `//Mortgage[@MtgInstrumentNumber=${instrLit}]`,
                    ) ??
                    selectSingleNodeCompat(
                        xmlDoc,
                        `//Mortgage[@MtgInstrumentNum=${instrLit}]`,
                    ) ??
                    selectSingleNodeCompat(xmlDoc, `//Mortgage[@RefNumber=${instrLit}]`);

                const lenderNode =
                    mtg?.selectSingleNode?.("Lender") ??
                    (mtg ? selectSingleNodeCompat(mtg, "Lender") : null);

                to = String(
                    lenderNode?.getAttribute?.("Email") ??
                        lenderNode?.getAttribute?.("FirmEmail") ??
                        "",
                ).trim();
            }
        }

        // const lawyerEmail = dataReaderObj.getVendorLawyerField();
        // const otherSideFirmContactEmail = getOtherSideFirmContactEmail();

        /*  ////EVA: follow up later
        setEmailDraft({
            
        });
        */
    }, [
        dataroot,
    ]);

    /* Follow Up button
    Enable Auto: advance undertaking file task (Redux). LastFollowUp* queued in Redux until Save flushes to XML.
    */
    // const handleFollowUp = useCallback((issue: UndertakingIssue) => {
    //     console.log(String(issue.defectId ?? "").trim());
    //     let advanced = false;
    //     if (mergeUndertakingEnableAuto === 1) {
    //         const baseTasks = inFileTasks ?? [];
    //         let tasksForAdvance = baseTasks;
    //         if (dataroot) {
    //             const rootLike = dataroot as XmlNodeLike;
    //             const xmlDoc = (rootLike.ownerDocument ?? dataroot) as
    //                 | Document
    //                 | XmlNodeLike;
    //             const undertakings = enrichUndertakingItemsWithPersistedTaskDues(
    //                 readUndertakingsFromXml(xmlDoc),
    //                 xmlDoc,
    //             );
    //             tasksForAdvance = mergeUndertakingTasks(
    //                 baseTasks,
    //                 undertakings,
    //                 {
    //                     firstDays: undertakingFirstDays ?? 60,
    //                     secDays: undertakingSecDays ?? 30,
    //                     complyDays: undertakingComplyDays ?? 60,
    //                     firstFollowEnable: mergeFirstFollowEnable as 0 | 1,
    //                     complyEnable: mergeComplyEnable as 0 | 1,
    //                     followUpTaskRemind: mergeFollowUpTaskRemind as 0 | 1,
    //                     complyTaskRemind: mergeComplyTaskRemind as 0 | 1,
    //                     enableAuto: mergeUndertakingEnableAuto as 0 | 1,
    //                 },
    //                 dataReaderObj,
    //                 dataReaderObj.getFirmContactID?.()?.[0],
    //             );
    //         }
    //         const { nextTasks, advanced: didAdvance } =
    //             advanceUndertakingFollowUpTask(tasksForAdvance, {
    //                 taskNotesKey: issue.taskNotesKey,
    //                 legacySyntheticKey:
    //                     issue.key !== issue.taskNotesKey ? issue.key : undefined,
    //             });
    //         advanced = didAdvance;
    //         if (advanced) {
    //             dispatch(setInfileTasks(nextTasks.map((t) => ({ ...t }))));
    //         }
    //     }

    //     const todayXml = dateToXMLDate(new Date());
    //     const defectIdForXml = String(issue.defectId ?? "").trim(); // Sending email manually 
    //     if (dataroot && defectIdForXml) {
    //         const followupNode = getUndertakingDataNode(defectIdForXml, dataroot);
    //         if (followupNode) {
    //             const sent1 = String(
    //                 followupNode.getAttribute?.("SentDate1") ?? "",
    //             ).trim();
    //             const followupNum = sent1 ? 2 : 1;
    //             finishFollowup(
    //                 followupNum,
    //                 dataroot,
    //                 defectIdForXml,
    //                 todayXml,
    //                 followUpActorName,
    //             );
    //         }
    //     }
    //     // Keep Data/@LastFollowUp* in sync immediately (do not wait for save flush).
    //     if (dataroot) {
    //         setUndertakingDefectDataAttributeByXmlIndex( // eeva: demo only, render update if click follow up; do not check email sent or not
    //             dataroot as XmlNodeLike,
    //             issue.xmlIndex,
    //             "LastFollowUpDate",
    //             todayXml,
    //         );
    //         setUndertakingDefectDataAttributeByXmlIndex( // eeva: demo only, render update if click follow up; do not check email sent or not 
    //             dataroot as XmlNodeLike,
    //             issue.xmlIndex,
    //             "LastFollowUpBy",
    //             followUpActorName,
    //         );
    //     }

    //     const payload = {
    //         lastFollowUpDate: todayXml,
    //         lastFollowUpBy: followUpActorName,
    //     };
    //     const merge: Record<string, typeof payload> = {};
    //     for (const k of [
    //         issue.taskNotesKey,
    //         undertakingTaskLinkKey(issue),
    //         issue.key,
    //     ]) {
    //         if (k) merge[k] = payload;
    //     }
    //     dispatch(mergeUndertakingPendingFollowUpXml(merge));

    //     if (advanced || dataroot) {
    //         runInnerFunction("SetPageDirty", null);
    //     }
    //     dispatch(setLastSaveTime(Date.now()));
    //     setUndertakingIssuesRevision((n) => n + 1);

    //     openToolbarSendEmailMain();
    // }, [
    //     mergeUndertakingEnableAuto,
    //     mergeFollowUpTaskRemind,
    //     mergeComplyTaskRemind,
    //     mergeFirstFollowEnable,
    //     mergeComplyEnable,
    //     undertakingFirstDays,
    //     undertakingSecDays,
    //     undertakingComplyDays,
    //     inFileTasks,
    //     dispatch,
    //     followUpActorName,
    //     dataroot,
    //     dataReaderObj,
    // ]);

    // const followup = (item) => {
    //     const FuE = item.SentDate2 ? 2 : item.SentDate1 ? 1 : 0;
    //     const node = getUndertakingDataNode(item.id, dataroot);
    //         if(node){
    //             node.setAttribute(`SentDate${FuE + 1}`, currDate);
    //             node.setAttribute(`SentBy${FuE + 1}`, followUpActorName);
    //             openToolbarSendEmailMain();
    //             // update();
    //         }
    // }

    // const handleUndertakingAutoSendEmail = useCallback(
    //     (issue: UndertakingIssue, enabled: boolean) => {
    //         if (!dataroot) return;
    //         const ok = setUndertakingDefectDataAttributeByXmlIndex(
    //             dataroot as XmlNodeLike,
    //             issue.xmlIndex,
    //             UNDERTAKING_XML_ATTR_AUTO_SEND_EMAIL,
    //             enabled ? "1" : "0",
    //         );
    //         if (!ok) return;
    //         runInnerFunction("SetPageDirty", null);
    //         setUndertakingIssuesRevision((n) => n + 1);
    //     },
    //     [dataroot],
    // );

    /** Auto-send email path
     * Frontend-Backend flow:
     * 1. User toggles "Send email automatically" in Undertaking dashboard.
     * 2. User be on the Undertaking Management page 
     * 3. Side note, on preference where auto sending email can be disabled and enabled - Only affect future tasks, such
     *    that step 1 and 2 will be gone trough by users and, hence, tracked accrodingly by frontend and backend.
     * 4. 
     * when today ≥ file-task due date, updates XML via `finishFollowup`, Redux/tasks, then
     * **calls the undertaking email backend** (`postUndertakingEmailApi` → `POST …/undertakingEmail`).
     */
    // useEffect(() => {
    //     const autoEmailDbg = isUndertakingAutoEmailDebugEnabled();
    //     const todayXml = dateToXMLDate(new Date());

    //     if (!dataroot || mergeUndertakingEnableAuto !== 1) return;

    //     const {
    //         tasksSnapshot,
    //         pendingMerge,
    //         anyFired,
    //     } = runAutoDueDayFollowUpPass({
    //         activeIssues,
    //         initialTasksSnapshot: mergedFileTasksForUndertakingDashboard,
    //         todayXml,
    //         followUpActorName,
    //         autoEmailDbg,
    //         logDebug: undertakingAutoEmailDebugLog,
    //         isMortgageDischargeIssue: isMortgageDischargeUndertakingIssue,
    //         getFollowupState: (defectId) => {
    //             const fu = getUndertakingDataNode(defectId, dataroot);
    //             return {
    //                 found: Boolean(fu),
    //                 sentDate1: String(fu?.getAttribute?.("SentDate1") ?? ""),
    //                 sentDate2: String(fu?.getAttribute?.("SentDate2") ?? ""),
    //             };
    //         },
    //         finishFollowup: (round, defectId, sentDateXml, sentBy) => {
    //             finishFollowup(
    //                 round,
    //                 dataroot,
    //                 defectId,
    //                 sentDateXml,
    //                 sentBy,
    //             );
    //         },
    //         postUndertakingEmail: (payload) =>
    //             postUndertakingEmailApi(SESSION_INFO, payload),
    //         setLastFollowUpOnIssue: (issue, sentDateXml, sentBy) => {
    //             setUndertakingDefectDataAttributeByXmlIndex(
    //                 dataroot as XmlNodeLike,
    //                 issue.xmlIndex,
    //                 "LastFollowUpDate",
    //                 sentDateXml,
    //             );
    //             setUndertakingDefectDataAttributeByXmlIndex(
    //                 dataroot as XmlNodeLike,
    //                 issue.xmlIndex,
    //                 "LastFollowUpBy",
    //                 sentBy,
    //             );
    //         },
    //         getPendingMergeKeys: (issue) => [
    //             issue.taskNotesKey,
    //             undertakingTaskLinkKey(issue),
    //             issue.key,
    //         ],
    //         advanceFollowUpTasks: (currentTasks, issue) =>
    //             advanceUndertakingFollowUpTask(currentTasks, {
    //                 taskNotesKey: issue.taskNotesKey,
    //                 legacySyntheticKey:
    //                     issue.key !== issue.taskNotesKey
    //                         ? issue.key
    //                         : undefined,
    //             }),
    //     });

    //     if (Object.keys(pendingMerge).length > 0) {
    //         dispatch(mergeUndertakingPendingFollowUpXml(pendingMerge));
    //     }
    //     if (tasksSnapshot !== mergedFileTasksForUndertakingDashboard) {
    //         dispatch(setInfileTasks(tasksSnapshot.map((t) => ({ ...t }))));
    //     }
    //     if (anyFired) {
    //         runInnerFunction("SetPageDirty", null);
    //         setUndertakingIssuesRevision((n) => n + 1);
    //     }
    // }, [
    //     activeIssues,
    //     dataroot,
    //     dispatch,
    //     followUpActorName,
    //     mergeUndertakingEnableAuto,
    //     mergedFileTasksForUndertakingDashboard,
    //     undertakingDashboardTodayKey,
    //     SESSION_INFO,
    // ]);

    // const handleUndertakingComplete = useCallback( // eeva Undertaking Card (Active); Comnplete button callback
    //     (issue: UndertakingIssue) => {
    //         if (!dataroot) return;
    //         const rootLike = dataroot as XmlNodeLike;
    //         const xmlDoc = (rootLike.ownerDocument ?? dataroot) as Document | XmlNodeLike;
    //         const nodes = selectNodesCompat(xmlDoc, "//Defect[@Undertaking='1']");
    //         const defectNode = nodes[issue.xmlIndex];
    //         if (!defectNode) return;
    //         const dataNode = selectSingleNodeCompat(defectNode, "Data");
    //         const today = dateToXMLDate(new Date());
    //         try {
    //             dataNode?.setAttribute?.("DateCompliedWith", today);
    //             dataNode?.setAttribute?.("CheckResolved", "1");
    //         } catch {
    //             /* ignore */
    //         }
    //         runInnerFunction("SetPageDirty", null); // user has update that requires clicking "Save" button
    //         const next = removeUndertakingLinkedTasks(
    //             inFileTasks ?? [],
    //             issue.taskNotesKey,
    //             issue.key,
    //         );
    //         dispatch(setInfileTasks(next.map((t) => ({ ...t })))); /// eeva: check shallow copy elsewhere; important 

    //         setUndertakingIssuesRevision((n) => n + 1);
    //     },
    //     [dataroot, dispatch, inFileTasks],
    // );

    // const completeUndertaking = (item) => {
    //     return () => {
    //         const node = dataroot.selectSingleNode(`Defect[@DefectId='${item.id}']`)?.selectSingleNode('Data');
    //         if(node){
    //             node.setAttribute('DateCompliedWith', currDate);
    //             // update();
    //         }
    //     }
    // }

    const handleNotify = useCallback((issue: UndertakingItemType) => {
        // Sale file: Buyer side lawyer.
        openEmailDraft("buyer", issue);
    }, [openEmailDraft]);

    // edit and navigate handler
    // const handleEdit = useCallback((
    //     issue: UndertakingIssueEditPayload,
    // ) => {
    //     // undertakingDebugLog("Undertaking edit — DefectId:", issue.defectId ?? "");
    //     openSpecificUndertakingIssueFromIssues(issue);
    // }, [openSpecificUndertakingIssueFromIssues]);

    // click and navigate handler
    // const handleIssuesLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    //     e.preventDefault();
    //     goToAllIssues(); // nav(destination)
    // }, [goToAllIssues]);

    // Opens Right Sidebar -> Notes -> auto-fill title, assignee (to current user) and date (today).
    const handleReminder = useCallback((issue: string) => {
        const today = new Date().toISOString().split("T")[0];
        rightSide.openTasksAddTask({
            taskDescription: `Reminder - ${issue}`,
            taskDate: today,
        });
    }, [rightSide]);

    // Opens Right Sidebar ->Notes -> filtered to show only "Issues".
    const handleNotes = useCallback(
        () => rightSide.openNotesWithFilter("Issues"),
        [rightSide],
    );

    // const activeEmptyState = useMemo(
    //     () => (
    //         <Alert
    //             variant="info"
    //             className="undertaking-info mb-0"
    //         >
    //             Enable undertakings within an issue from the{" "}
    //             <a
    //                 href="#"
    //                 onClick={handleIssuesLinkClick}
    //                 className="undertaking-link"
    //             >
    //                 Issues screen
    //             </a>
    //             .
    //         </Alert>
    //     ),
    //     [handleIssuesLinkClick],
    // );

    // const completedEmptyState = useMemo(
    //     () => (
    //         <Alert                
    //             variant="info"
    //             className="undertaking-info mb-0"
    //         >
    //             Completed undertakings will appear here.
    //         </Alert>
    //     ),
    //     [],
    // );

    // ——— Handlers: legacy doc Generate / Open / Email ———
    // const handleGenerate = (doc: UndertakingDocumentRow) => {
    //     const target = LEGACY_DOCUMENT_GENERATE[doc.name];
    //     if (!target) {
    //         alert(`No legacy document mapping found for "${doc.name}".`);
    //         return;
    //     }

    //     const id = currentApp.id;
    //     const PSM = currentApp.PSM;
    //     if (!id || id === CONST.uninitialized) {
    //         alert("No active file context found (missing AppID). Open a file and try again.");
    //         return;
    //     }


    //     if (target.doctypeId) {
    //         setGeneratingDocName(doc.name);
    //         const tracker = Date.now();
    //         activityTrackerRef.current = tracker;
    //         activityCenterAdd(doc.name, "load", tracker);
    //         if (generateUiTimeoutRef.current) {
    //             window.clearTimeout(generateUiTimeoutRef.current);
    //         }
    //         // Absolute failsafe: never let the UI spin forever if the hidden dialog fails to init.
    //         generateUiTimeoutRef.current = window.setTimeout(() => {
    //             setGeneratingDocName(null);
    //             generateUiTimeoutRef.current = null;
    //             if (activityTrackerRef.current) {
    //                 activityCenterDelete(activityTrackerRef.current);
    //                 activityCenterAdd(
    //                     `${doc.name}: generation did not start (timed out).`,
    //                     "error",
    //                     Date.now(),
    //                 );
    //                 activityTrackerRef.current = null;
    //             }
    //         }, 30000);
    //         // Reuse existing in-file legacy frame (already loaded behind React screens).
    //         // This avoids opening any new dialogs/popups.
    //         const hardTimeout = window.setTimeout(() => {
    //             setGeneratingDocName(null);
    //             if (generateUiTimeoutRef.current) {
    //                 window.clearTimeout(generateUiTimeoutRef.current);
    //                 generateUiTimeoutRef.current = null;
    //             }
    //             if (activityTrackerRef.current) {
    //                 activityCenterDelete(activityTrackerRef.current);
    //                 activityCenterAdd(
    //                     `${doc.name}: generation did not start (timed out).`,
    //                     "error",
    //                     Date.now(),
    //                 );
    //                 activityTrackerRef.current = null;
    //             }
    //         }, 15000);

    //         openAndGenerateLegacyDocType({
    //             doctypeId: target.doctypeId,
    //             groupingId: target.groupingId,
    //             expectedAppId: id,
    //             options: { tries: 80, intervalMs: 100 },
    //             onTriggered: () => {
    //                 window.clearTimeout(hardTimeout);
    //                 setGeneratingDocName(null);
    //                 setOpenableDocs((prev) => {
    //                     const next = { ...prev };
    //                     delete next[doc.name];
    //                     return next;
    //                 });
    //                 if (generateUiTimeoutRef.current) {
    //                     window.clearTimeout(generateUiTimeoutRef.current);
    //                     generateUiTimeoutRef.current = null;
    //                 }
    //                 if (activityTrackerRef.current) {
    //                     activityCenterDelete(activityTrackerRef.current);
    //                     activityTrackerRef.current = null;
    //                 }
    //                 activityCenterAdd(
    //                     `${doc.name}: generation started.`,
    //                     "success",
    //                     Date.now(),
    //                 );
    //             },
    //         });
    //     } else {
    //         // Not implemented for this row yet (no legacy doctype mapping).
    //         alert(`No legacy doctype mapping found for "${doc.name}".`);
    //     }
    // };

    // const handleOpen = (doc: UndertakingDocumentRow) => {
    //     const target = LEGACY_DOCUMENT_GENERATE[doc.name];
    //     if (!target?.doctypeId) return;
    //     if (openableDocs[doc.name] === false) return;

    //     const id = currentApp.id;
    //     if (!id || id === CONST.uninitialized) return;

    //     const tracker = Date.now();
    //     activityCenterAdd(`${doc.name}: opening…`, "load", tracker);

    //     openLegacyGeneratedDocByDoctypeId({
    //         doctypeId: target.doctypeId,
    //         groupingId: target.groupingId,
    //         expectedAppId: id,
    //         options: { tries: 60, intervalMs: 150 },
    //         onOpened: () => {
    //             setOpenableDocs((prev) => ({ ...prev, [doc.name]: true }));
    //             activityCenterDelete(tracker);
    //             activityCenterAdd(`${doc.name}: opened.`, "success", Date.now());
    //         },
    //         onNotGenerated: () => {
    //             activityCenterDelete(tracker);
    //             setOpenableDocs((prev) => ({ ...prev, [doc.name]: false }));
    //             activityCenterAdd(
    //                 `${doc.name}: no generated document found.`,
    //                 "error",
    //                 Date.now(),
    //             );
    //         },
    //     });
    // };

    const handleEmail = (doc: UndertakingDocumentRow) => {
        const target = LEGACY_DOCUMENT_GENERATE[doc.name];
        if (!target?.doctypeId) return;
        if (openableDocs[doc.name] === false) return;

        const id = currentApp.id;
        if (!id || id === CONST.uninitialized) return;

        const tracker = Date.now();
        activityCenterAdd(`${doc.name}: opening email…`, "load", tracker);

        emailLegacyGeneratedDocByDoctypeId({
            doctypeId: target.doctypeId,
            groupingId: target.groupingId,
            expectedAppId: id,
            options: { tries: 60, intervalMs: 150 },
            onEmailed: () => {
                setOpenableDocs((prev) => ({ ...prev, [doc.name]: true }));
                activityCenterDelete(tracker);
                activityCenterAdd(`${doc.name}: email opened.`, "success", Date.now());
            },
            onNotGenerated: () => {
                activityCenterDelete(tracker);
                setOpenableDocs((prev) => ({ ...prev, [doc.name]: false }));
                activityCenterAdd(
                    `${doc.name}: no generated document found.`,
                    "error",
                    Date.now(),
                );
            },
            onNotSupported: () => {
                activityCenterDelete(tracker);
                activityCenterAdd(
                    `${doc.name}: email action not available here.`,
                    "error",
                    Date.now(),
                );
            },
        });
    };

    const openEmail = (defectId: string, num: number, user: string, mode: "followup" | "notify" = "followup") => {
        let dNode = dataroot.selectSingleNode(`Defect[@DefectId="${defectId}"]`);
        let data = dNode?.selectSingleNode(`Data`);
        let emailNode = dataroot.selectSingleNode("EmailSystem");
        if(emailNode){
            dataroot.removeChild(emailNode);
        }
        let tmpNode = dataroot.ownerDocument.createElement('EmailSystem');
        emailNode = dataroot.appendChild(tmpNode);

        if(mode === "followup"){
            let tmpNode2 = dataroot.ownerDocument.createElement('Followup');
            let followData = emailNode.appendChild(tmpNode2);
            followData.setAttribute("DefectId", defectId);
            followData.setAttribute("FollowupDone", num);
            followData.setAttribute("User", user);  
        }

        // Populate To/CC for every issue type (body + subject are Discharge-only below).
        const mailList = getMailList(dataroot, dataroot.getAttribute("PSM"), defectId, mode);
        emailNode.setAttribute("to", mailList.to.join("; "));
        if(mailList.cc.length){
            emailNode.setAttribute("showCC", '1');
            emailNode.setAttribute("EmailCC", mailList.cc.join("; "));
        }
        if(mailList.bcc.length){
            emailNode.setAttribute("showBCC", '1');
            emailNode.setAttribute("EmailBCC", mailList.bcc.join("; "));
        }
        // only populate for mortgage discharge
        if(dNode.getAttribute("type") === "32_2"){
            // Subject is driven by the firm's Undertaking Preferences template choice.
            // Modes: Sale Notify → buyerSubject; Purchase Follow Up → sellerSubject; Sale/Mortgage Follow Up → lenderSubject.
            const subjectTemplate =
                mode === "notify" ? (undertakingPrefs?.buyerSubject || "[Reference Line] and [Property Address]")
                : PSM === "P" ? (undertakingPrefs?.sellerSubject || "[Reference Line] and [Property Address]")
                : (undertakingPrefs?.lenderSubject || "[Mortgage Reference Number] and [Property Address]");
            const placeholderValues: Record<string, string> = {
                "[Reference Line]": dataroot.getAttribute("ReLine") || "",
                "[File Number]": dataroot.getAttribute("MatterNumber") || "",
                "[Property Address]": propertyAddress || "",
                "[Mortgage Reference Number]": data?.getAttribute("MortgageReferenceNo") || "",
                "[Borrower]": dataReaderObj.getMortgagorName?.() || dataroot.getAttribute("ClientName") || "",
            };
            const subj = Object.keys(placeholderValues).reduce(
                (acc, key) => acc.split(key).join(placeholderValues[key]),
                subjectTemplate,
            );

            // const mailList = getMailList(dataroot, dataroot.getAttribute("PSM"), defectId, mode);

            let emailBody: string;
            if(mode === "notify"){
                // Buyer-side Comply template (Sale Notify) — mirrors UndertakingSettings showBuyerPreview.
                const chargee = data?.getAttribute("Lender") || data?.getAttribute("LenderName") || "[Chargee]";
                const mortgageInstrumentNum = data?.getAttribute("InstrumentNumber");
                const mortgageInstrument = mortgageInstrumentNum
                    || data?.getAttribute("TeranetInstrumentNum")
                    || data?.getAttribute("RegistrationNumber")
                    || "[Instrument Number]";
                const dischargeInstrument = dNode?.getAttribute("DischargeInstrumentNumber") || "[Discharge Registration Number]";
                const dischargeDate = dNode?.getAttribute("DischargeRegistrationDate") || "[Discharge Registration Date]";
                const relatedSecurityNodes = mortgageInstrumentNum
                    ? (dataroot.selectNodes(`Defect[Data/@AssociatedRefNum='${mortgageInstrumentNum}']`) || [])
                        .filter((n: any) => n.getAttribute("DefectId") !== defectId)
                    : [];
                const hasRelatedSecurity = relatedSecurityNodes.length > 0;
                const relatedSecurityInstrument = relatedSecurityNodes
                    .map((n: any) => {
                        const relData = n.selectSingleNode("Data");
                        return relData?.getAttribute("InstrumentNumber")
                            || relData?.getAttribute("TeranetInstrumentNum")
                            || relData?.getAttribute("RegistrationNumber");
                    })
                    .filter(Boolean)
                    .join(", ") || "[Instrument Number]";
                const relatedSecurityClause = hasRelatedSecurity
                    ? ` and the related security interest ${relatedSecurityInstrument}`
                    : "";
                const dischargeVerb = hasRelatedSecurity ? "were" : "was";
                // Item #2 lists OTHER mortgage discharge undertakings still pending — only shown when there is more than 1 mortgage to be discharged on this file.
                const otherDischarges = (dataroot.selectNodes("Defect[@Undertaking='1' and @type='32_2']") || [])
                    .filter((n: any) => n.getAttribute("DefectId") !== defectId)
                    .filter((n: any) => !(n.selectSingleNode("Data")?.getAttribute("DateCompliedWith") > ""));
                const pendingListItem = otherDischarges.length > 0
                    ? `<li>As of today's date, we have not yet received discharge particulars or confirmation of the following:
                    <div style="padding-left:2rem">${otherDischarges.map((n: any) => buildDesc(n)).join("<br/>")}</div>
                    </li>`
                    : "";
                const completionLine = `<br/><p>It is confirmed that this completes this matter, as this fulfils the undertaking provided on closing.</p>`;
                emailBody =
                `<p>Further to the closing of the above-noted transaction which took place on ${closing || "[Closing Date]"}, and pursuant to the undertaking given thereon, please be advised of the following:</p>
                <ol>
                <li>A discharge of mortgage number ${mortgageInstrument} in favour of ${chargee}${relatedSecurityClause} ${dischargeVerb} registered as instrument number ${dischargeInstrument} on ${dischargeDate}.</li>
                ${pendingListItem}
                </ol>
                ${completionLine}`
            } else if(PSM === "P"){
                // Seller-side Follow Up template.
                emailBody =
                `<p>Further to the closing of the above-noted transaction, which took place on ${closing || "[Closing Date]"}, your Undertaking was provided for the following:</p>
                <ul>
                <li>${buildDesc(dNode)}</li>
                </ul>
                <p>As of today's date, we have not yet received discharge particulars or confirmation of the following in accordance with your Undertaking:</p>
                <ul>
                <li>${buildDesc(dNode)}</li>
                </ul>
                <p>In accordance with your undertaking given on closing, please advise if the undertaking items as described above has/have been registered and complied with, and provide particulars of same at your earliest convenience.</p>`
            } else {
                // Lender-side Follow Up template (Sale/Mortgage, all undertakings).
                emailBody =
                `<p>Further to previous correspondence with you in connection with Mortgage Reference Number: ${data?.getAttribute("MortgageReferenceNo") || "[Mortgage Reference Number]"}, wherein sufficient funds to pay out the mortgage were forwarded to you, the following have not yet been received:</p>
                <ul>
                <li>Discharge of Charge/Mortgage.</li>
                <li>Release of interest in insurance policy.</li>
                </ul>
                <p>Please forward the above documents to the attention of the undersigned at your earliest possible convenience.</p>
                <p>Thank you in advance for your prompt attention to this matter.</p>`
            }

            // emailNode.setAttribute("to", mailList.to.join("; "));
            emailNode.setAttribute("subject", subj);
            emailNode.setAttribute("issubjectOverWrite", '1');
            // if(mailList.cc.length){
            //     emailNode.setAttribute("showCC", '1');
            //     emailNode.setAttribute("EmailCC", mailList.cc.join("; "));
            // }
            const body = emailNode.ownerDocument.createElement("EmailClause");
            body.setAttribute("content", emailBody);
            emailNode.appendChild(body);
        }
        runInnerFunction("SendEmailMain", null);
    }

    const formatDate = (str: string) => {
        return FormatDateDataType(str, "dfca")
    }

    const UndertakingPanel = (props: {items: UndertakingItemType[], mode : "active" | "completed"}) => {
        return <>
            {props.items.map((item, index) => {
                const FollowupsDone = item.SentDate2 ? 2 : item.SentDate1 ? 1 : 0;
                // console.log(FuE)
                const nextSend = FollowupsDone < 2 ? item[`Date${FollowupsDone + 1}`] : "";
                let history = JSON.parse(item.followupHistory || "[]");
                const fullHist = [...[{date: item.SentDate1, by: item.SentBy1}, {date: item.SentDate2, by: item.SentBy2}], ...history].filter((i) => i.date > "");
                let notifyHist: {date: string, by: string}[] = JSON.parse(item.notifyHistory || "[]");
                const lastNotify = notifyHist.length ? notifyHist[notifyHist.length - 1] : null;
                const lastFollow = fullHist.length ? fullHist[fullHist.length - 1] : null;
                // console.log(history, fullHist)
                // console.log(item)
                return(
                    <Card className="optionsHover mb-1" key={index}>
                        <Card.Body>
                            <Row>
                                <Col className="flex-grow-1" style={{ minWidth: '0'}}>
                                    <Card.Title className="text-truncate">{item.name}</Card.Title>
                                </Col>
                                
                                {autoEnabled && props.mode === "active" && FollowupsDone < 2 && item.CanAutoFollowUp && nextSend && (() => {
                                    const daysUntil = differenceInDays(dateToXMLDate(new Date()), nextSend);
                                    const label = daysUntil >= 1 && daysUntil <= 10
                                        ? `Auto-email will send in ${daysUntil} Day${daysUntil === 1 ? "" : "s"}`
                                        : `Auto-email on ${formatDate(nextSend)}`;
                                    return <Col xs="auto"><Badge bg="info" pill>{label}</Badge></Col>;
                                })()}
                                
                                <Col xs="auto" className="text-nowrap text-end">
                                    <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 500, hide: 0 }}
                                        overlay={
                                            <Tooltip id="notes-tooltip">
                                                Edit undertaking
                                            </Tooltip>
                                        }
                                        >
                                        <Button
                                            type="button"
                                            variant="link-text"
                                            className="optionItem"
                                            onClick={() => editDefect(item.id)}
                                        >
                                            <i className="fa-solid fa-pen" />
                                        </Button>
                                    </OverlayTrigger>

                                    <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 500, hide: 0 }}
                                        overlay={
                                            <Tooltip>
                                                Add a task
                                            </Tooltip>
                                        }
                                        >
                                        <Button
                                            type="button"
                                            variant="link-text"
                                            className="optionItem"
                                            aria-label="Reminder"
                                            onClick={() => handleReminder(item.name)}
                                        >
                                            <i className="fa-solid fa-bell" />
                                        </Button>
                                    </OverlayTrigger>

                                    <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 500, hide: 0 }}
                                        overlay={
                                            <Tooltip>
                                                View Notes
                                            </Tooltip>
                                        }
                                        >
                                        <Button
                                            type="button"
                                            variant="link-text"
                                            className="optionItem"
                                            aria-label="Notes"
                                            onClick={handleNotes}
                                        >
                                            <i className="fa-solid fa-note-sticky" />
                                        </Button>
                                    </OverlayTrigger>
                                </Col>
                            </Row>

                            {props.mode === "active" && item.emailOk != undefined && !item.emailOk && <>
                                <Alert variant="danger">
                                    Email not found. Please add an email address to send automatic follow up emails.
                                </Alert>
                            </>}
                            {/* {item.CanAutoFollowUp &&
                                <Row className="g-3 mb-3">
                                    <Col>
                                        <Form.Label>Lender</Form.Label>
                                        {item.lender || "Unknown"}
                                    </Col>
                                    <Col>
                                        <Form.Label>Instrument Number</Form.Label>
                                        {item.instrumentNumber || "Unknown"}
                                    </Col>
                                </Row>
                            }     */}
                            <Row className="g-3 mb-3">
                                <Col xs={12}>
                                    {/* <Form.Control
                                        plaintext
                                        readOnly
                                        className="p-0"
                                        placeholder=" "
                                        tabIndex={-1}
                                        value={item.desc}
                                    /> */}
                                    {/* {item.desc || " "} */}
                                    <IssueDesc text={item.desc || " "}/>
                                </Col>
                            </Row>

                            {/* {mode === "completed" && issue.completedNumber && (
                                <Form.Group className="mb-2">
                                    <Form.Label>{UNDERTAKING_COMPLETED_NUMBER_LABEL}</Form.Label>
                                    <Form.Control plaintext defaultValue={issue.completedNumber}/>
                                </Form.Group>
                            )} */}

                            <Row className="g-3 mb-3">
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Due</Form.Label>
                                        <Form.Control
                                            readOnly
                                            plaintext
                                            placeholder="Add a closing date.."
                                            className="p-0"
                                            value={formatDate(item.DueDate)} 
                                        />
                                    </Form.Group>
                                </Col>

                                {props.mode === "completed" && (
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>Complied</Form.Label>
                                            <Form.Control
                                                readOnly
                                                plaintext
                                                className="p-0"
                                                defaultValue={formatDate(item.CompletedDate)}
                                            />
                                            <Form.Text>
                                                {`By ${item.CompletedBy || "unknown"}`}
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}

                                {props.mode === "active" &&
                                    <Col>
                                        <Form.Label>Last Follow Up</Form.Label>

                                        <Row className="gx-1">

                                            <>
                                                {FollowupsDone ? (
                                                    <Col xs="auto">
                                                        <OverlayTrigger trigger="click" rootClose overlay={
                                                            <Popover style={{ maxWidth: "none" }}>
                                                                <Popover.Body style={{maxHeight: '200px', }} className="overflow-auto">
                                                                    <Table>
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Date</th>
                                                                                <th>By</th>
                                                                            </tr>
                                                                        </thead>

                                                                        <tbody>
                                                                            {fullHist.length?
                                                                                <>
                                                                                    {fullHist.map((n, i) => (
                                                                                        <tr key={i}>
                                                                                            <td>{formatDate(n.date)}</td>
                                                                                            <td>{n.by}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </>
                                                                                :
                                                                                <Form.Label>No follow ups</Form.Label>
                                                                            }
                                                                        </tbody>
                                                                    </Table>
                                                                </Popover.Body>
                                                            </Popover>
                                                            }>
                                                                    <i className="fa-solid fa-clock-rotate-left" />
                                                        </OverlayTrigger>
                                                    </Col>
                                                )
                                                :
                                                null
                                                }
                                            </>

                                            <Col>
                                                <Form.Control
                                                    plaintext
                                                    readOnly
                                                    className="p-0"
                                                    placeholder="No follow ups"
                                                    tabIndex={-1}
                                                    value={lastFollow ? formatDate(lastFollow.date) : ""}
                                                />
                                            </Col>

                                            {lastFollow ? (
                                                <Col xs={12}>
                                                    <Form.Text className="small text-muted">
                                                        By {lastFollow.by}
                                                    </Form.Text>
                                                </Col>
                                            ) : null}
                                        </Row>
                                    </Col>
                                }

                                {props.mode === "completed" && PSM === "S" &&
                                    <Col>
                                        <Form.Label>Notified</Form.Label>

                                        <Row className="gx-1">
                                            {lastNotify ? (
                                                <Col xs="auto">
                                                    <OverlayTrigger trigger="click" rootClose overlay={
                                                        <Popover style={{ maxWidth: "none" }}>
                                                            <Popover.Body style={{maxHeight: '200px'}} className="overflow-auto">
                                                                <Table>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Date</th>
                                                                            <th>By</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {notifyHist.map((n, i) => (
                                                                            <tr key={i}>
                                                                                <td>{formatDate(n.date)}</td>
                                                                                <td>{n.by}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </Popover.Body>
                                                        </Popover>
                                                    }>
                                                        <i className="fa-solid fa-clock-rotate-left" />
                                                    </OverlayTrigger>
                                                </Col>
                                            ) : null}

                                            <Col>
                                                <Form.Control
                                                    plaintext
                                                    readOnly
                                                    className="p-0"
                                                    placeholder="Not yet notified"
                                                    tabIndex={-1}
                                                    value={lastNotify ? formatDate(lastNotify.date) : ""}
                                                />
                                            </Col>

                                            {lastNotify ? (
                                                <Col xs={12}>
                                                    <Form.Text className="small text-muted">
                                                        By {lastNotify.by}
                                                    </Form.Text>
                                                </Col>
                                            ) : null}
                                        </Row>
                                    </Col>
                                }
                            </Row>

                            {props.mode === "completed" && item.isMortgageDischarge &&
                                <Row className="g-3 mb-3">
                                    <Col xs={6}>
                                        <Form.Group>
                                            <Form.Label>Registration #</Form.Label>
                                            <Form.Control
                                                key={item.registeredSecurityNum || ""}
                                                defaultValue={item.registeredSecurityNum || ""}
                                                placeholder="ex. 1234-9876-12"
                                                onBlur={(e) => {
                                                    setDefectAttrs(item.id, { DischargeInstrumentNumber: e.target.value });
                                                    update();
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={6}>
                                        <Form.Group>
                                            <Form.Label>Registration Date</Form.Label>
                                            <DatePicker
                                                date={item.registeredSecurityDate || ""}
                                                setDate={(newDate) => {
                                                    setDefectAttrs(item.id, {
                                                        DischargeRegistrationDate: newDate,
                                                        isDischargeRegistrationDateOverWrite: "1",
                                                    });
                                                    update();
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            }

                            {!!item.relatedSecurityCharges?.length &&
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <Form.Label><b>Related Security Charge(s)</b></Form.Label>
                                    </Col>
                                    {item.relatedSecurityCharges.map((rel) => (
                                        <Col xs={12} key={rel.id}>
                                            <Button
                                                variant="link-text"
                                                className="p-0"
                                                onClick={() => editDefect(rel.id)}
                                            >
                                                <i className="fa-solid fa-link me-2" />
                                                {rel.title}
                                            </Button>
                                        </Col>
                                    ))}
                                </Row>
                            }

                            {item.CanAutoFollowUp && currentApp.PSM !== "P" && props.mode !== "completed" &&
                                <>
                                    <Row className="mb-2">
                                        <Col>
                                            <Form.Label>Discharge Escalation Email</Form.Label>
                                        </Col>
                                  
                                        {/* <Col className="text-end">
                                            <a className="btn-link-text me-2" target="_blank" href="https://llc.firstcdn.com/Profile/Login.aspx">
                                                <i className="fa-regular fa-arrow-up-right-from-square"></i>
                                                LLC
                                            </a>
                                            <a className="btn-link-text" target="_blank" href="https://login.assystrealestate.com/are/Account/Login">
                                                <i className="fa-regular fa-arrow-up-right-from-square"></i>
                                                Lender Centre
                                            </a>
                                        </Col> */}

                                        <Col xs={12}>
                                            <Form.Control 
                                                defaultValue={item.escalationEmail} 
                                                placeholder="enter email address.."
                                                onBlur={(e) => {
                                                    let nod = dataroot.selectSingleNode(`Defect[@DefectId='${item.id}']`)?.selectSingleNode('Data')?.selectSingleNode('Lender')
                                                    nod?.setAttribute('EscalationEmail', e.target.value)
                                                    update()
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            }

                            {props.mode === "active" && (
                                <div className="text-end">
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => {
                                            // const node = getUndertakingDataNode(item.id, dataroot);
                                            // if(FollowupsDone < 2 && node){
                                            //     node.setAttribute(`SentDate${FollowupsDone + 1}`, currDate);
                                            //     node.setAttribute(`SentBy${FollowupsDone + 1}`, user)
                                            //     update();
                                            // } else if (node) {
                                            //     history.push({date: currDate, by: user});
                                            //     node.setAttribute("FollowupHistory", JSON.stringify(history));
                                            //     update();
                                            // }
                                            // email
                                            openEmail(item.id, FollowupsDone, user)
                                        }}
                                    >
                                        Follow Up
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={() => handleCompleteClick(item)}
                                    >
                                        Complete
                                    </Button>
                                </div>
                            )}

                            {props.mode === "completed" && PSM === "S" &&
                                <div className="text-end">
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => {
                                            const node = getUndertakingDataNode(item.id, dataroot);
                                            if(node){
                                                notifyHist.push({date: currDate, by: user});
                                                node.setAttribute("NotifyHistory", JSON.stringify(notifyHist));
                                                update();
                                            }
                                            openEmail(item.id, FollowupsDone, user, "notify");
                                        }}
                                    >
                                        Notify
                                    </Button>
                                </div>
                            }
                        </Card.Body>
                    </Card>
                )
                }
            )}
        </>
    }

    return (
        <>
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Vendor's Solicitor's Undertakings</h1>
                </Col>

                <Col xs="auto">
                    <Form.Check
                        type="switch"
                        id="e"
                        label="Send email automatically for this file"
                        style={{ display: 'inline-flex', gap: '8px'}}
                        checked={sendCheck}
                        onChange={() => {
                            dataroot.setAttribute("DisableUndertakingEmail", sendCheck ? "1" : "0");
                            // dataroot.setAttribute("UndertakingEmailOverwrite", "1");
                            setSendCheck(!sendCheck);
                            runInnerFunction("SetPageDirty", null);
                        }}
                        // disabled={!undertakingPrefs.emailFu || !undertakingPrefs.enableFu}
                    />
                </Col>

                <Col xs="auto">
                    <OverlayTrigger 
                        trigger="click" 
                        placement="bottom"
                        rootClose 
                        overlay={
                        <Popover style={{ minWidth: '540px' }}>
                            <Popover.Body style={{maxHeight: '200px', minWidth: '400px' }} className="overflow-auto">
                                <h3>Documents</h3>
                                {DOCUMENTS.map((doc) => (
                                    <Row key={doc.name}>
                                        <Col xs={6}>
                                            {doc.name}
                                        </Col>
                                        <Col xs={6} className="justify-content-end">
                                            {(() => {
                                                const target = LEGACY_DOCUMENT_GENERATE[doc.name];
                                                const canRun =
                                                    Boolean(target) &&
                                                    Boolean(target?.doctypeId) &&
                                                    Boolean(currentApp.id) &&
                                                    currentApp.id !== CONST.uninitialized;
                                                return (
                                                    <Button
                                                        variant="link-text"
                                                        disabled={!canRun || generatingDocName === doc.name}
                                                        // onClick={() => handleGenerate(doc)}
                                                        onClick={() => genDocument(doc.id)}
                                                        title={
                                                                canRun
                                                                    ? generatingDocName === doc.name
                                                                        ? "Generating…"
                                                                        : "Generate document"
                                                                    : target?.doctypeId
                                                                    ? "Open a file first to generate"
                                                                    : "Not implemented yet"
                                                            }
                                                    >
                                                        {generatingDocName === doc.name ? (
                                                                <i className="fa-solid fa-spinner fa-spin me-2" />
                                                            ) : (
                                                                <i className="fa-solid fa-file-lines me-2" />
                                                            )}
                                                            {generatingDocName === doc.name
                                                                ? "Generating…"
                                                                : "Generate"}
                                                    </Button>
                                                );
                                            })()}
                                            <Button
                                                variant="link-text"
                                                disabled={
                                                    !currentApp.id ||
                                                    currentApp.id === CONST.uninitialized ||
                                                    !LEGACY_DOCUMENT_GENERATE[doc.name]?.doctypeId ||
                                                    openableDocs[doc.name] === false
                                                }
                                                onClick={() => handleEmail(doc)}
                                                title={
                                                    openableDocs[doc.name] === false
                                                        ? "No generated document"
                                                        : "Email generated document"
                                                }
                                            >
                                                <i className="fa-solid fa-paper-plane me-2" />
                                                Email
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                            </Popover.Body>
                        </Popover>
                        }>
                            <Button variant="outline-primary">
                                Documents
                                <i className="fa-solid fa-caret-down ms-2" />
                            </Button>
                    </OverlayTrigger>
                </Col>
            </Row>

            <Row className="g-3">
                <Col md={6}>
                    <h2 className="mb-2">    
                        <i className="fa-solid fa-circle-exclamation status-warning me-2" />
                        Active
                    </h2>

                    { activeUndertakings.length == 0 &&
                        <Alert
                            variant="info"
                            className="mb-0"
                        >
                            Enable undertakings within an issue from the{" "}
                            <a
                                onClick={() => {
                                    let navObj: NavObjectType = {
                                        component: "main",
                                        url: "lfmaster.asp",
                                        params: {
                                            "tb": "TPAppXML",
                                            "formasp": "purfileedit",
                                            "id": dataReaderObj.getAppId(),
                                            "PSM": dataReaderObj.getPSM(),
                                        },
                                        controller: {tabName: "Issues", tabNumber: 0},
                                        label: "Issues",
                                    }
                                    nav(navObj);
                                }}
                            >
                                Issues screen
                            </a>
                            .
                        </Alert>
                    }
                    <UndertakingPanel items={activeUndertakings} mode="active"/>
                </Col>

                <Col md={6}>
                    <h2 className="mb-2">    
                        <i className={"fa-solid fa-circle-check status-success me-2"} />
                        Completed
                    </h2>

                    {completedUndertakings.length == 0 &&
                        <Alert
                            variant="info"
                            className="undertaking-info mb-0"
                        >
                            Completed undertakings will appear here
                        </Alert>
                    }
                    
                    <UndertakingPanel items={completedUndertakings} mode="completed"/>
                </Col>
            </Row>

            <Modal show={!!regPrompt} onHide={() => setRegPrompt(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Registration Details?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Would you like to add Registration details to complete this undertaking?</p>
                    <Row className="g-3">
                        <Col xs={6}>
                            <Form.Group>
                                <Form.Label>Registration #</Form.Label>
                                <Form.Control
                                    value={regNumInput}
                                    placeholder="ex. 1234-9876-12"
                                    onChange={(e) => setRegNumInput(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6}>
                            <Form.Group>
                                <Form.Label>Registration Date</Form.Label>
                                <DatePicker
                                    date={regDateInput}
                                    setDate={setRegDateInput}
                                    placeholder="ex. 1234-9876-12"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => resolveRegPrompt(false)}>Cancel</Button>
                    <Button variant="primary" onClick={() => resolveRegPrompt(true)}>Complete</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!completePrompt} onHide={() => setCompletePrompt(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Complete Undertaking</Modal.Title>
                </Modal.Header>
                <Modal.Body>{completePrompt && COMPLETE_PROMPT_MESSAGE[completePrompt.kind]}</Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setCompletePrompt(null)}>Cancel</Button>
                    {completePrompt?.kind === "discharge-simple" ? (
                        <Button variant="primary" onClick={() => resolveCompletePrompt(true)}>Complete</Button>
                    ) : (
                        <>
                            <Button variant="outline-primary" onClick={() => resolveCompletePrompt(false)}>No</Button>
                            <Button variant="primary" onClick={() => resolveCompletePrompt(true)}>Yes</Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            <Modal show={showNotifyModal} onHide={() => setShowNotifyModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Notify Buyer's Lawyer?</Modal.Title>
                </Modal.Header>
                <Modal.Body>Would you like to send a completion notification email to the buyer's lawyer for this undertaking?</Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setShowNotifyModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={() => {
                        setShowNotifyModal(false);
                        openEmail(activeItem.id, 0, user, "notify");
                    }}>Notify</Button>
                </Modal.Footer>
            </Modal>

            {emailDraft && (
                <UndertakingEmailModal
                    show={true}
                    onHide={() => setEmailDraft(null)}
                    draft={emailDraft}
                    setDraft={(next) => setEmailDraft(next)}
                />
            )}
        </>
    );
};

export default Undertaking;